import { useContext, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';
import { CountryCodes } from '@onevesthq/ov-enums';
import omitDeep from 'omit-deep-lodash';
import { ResidencyInformationVisual } from './residencyInformation.visual';
import { ForeignTaxInformation } from '../../../../../interfaces/foreignTaxInformation';
import { SubStepTypes, User } from '../../../../../interfaces';
import { usePermissions, UserContext } from '../../../../../providers/userContextProvider';
import { CustomField, FETCH_CUSTOM_FIELDS, modifyUpdatingCustomFields } from '../../../../../interfaces/customField';
import { getEnabledCustomFields } from '../utils';

const FETCH_USER_FIELDS = (permissions: string[], derivedCustomFields?: string[]) => `#graphql
  user {
    id
    type
    citizenships
    foreignTaxExists
    foreignTaxInformation {
      id
      foreignTaxCountry
      foreignTaxNumber
      ${permissions.includes('read:client_taxid_disclosed') ? 'foreignTaxNumberDisclosed' : ''}
    }
    countryOfTaxResidence
    yearEnd
    taxNumber
    taxIdType
    taxId
    ${permissions.includes('read:client_taxid_disclosed') ? 'taxIdDisclosed' : ''}
    fatcaStatus
    fatcaStatusOtherDescription
    organization {
      applicableLocalization {
        countries
      }
    }
    ${derivedCustomFields ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
      key
      value
      customField { id translatedName { en fr } translatedDescription{ en fr} type format}
      selectedOptions { value displayText { en fr } }
    }` : ''}
  }
`;

const FETCH_USER = (permissions: string[], derivedCustomFieldsKeys?: string[]) => gql`
  query fetchUserResidencyInformation($userId: ObjectID!) {
    fetchUser(userId: $userId) {
      ${FETCH_USER_FIELDS(permissions, derivedCustomFieldsKeys)}
    }
  }
`;

const UPDATE_USER = (permissions: string[], derivedCustomFieldsKeys?: string[]) => gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      ${FETCH_USER_FIELDS(permissions, derivedCustomFieldsKeys)}
    }
  }
`;

export const ResidencyInformation = ({
  options, userId, onNext, stepLoading, workflowCompletion, grid = false, updateMode = false,
  autoSelectCountryOfTaxResidence = true,
}: {
  options: any, userId: string, onNext: () => void, stepLoading: boolean, workflowCompletion?: any, grid?: boolean, updateMode?: boolean,
  autoSelectCountryOfTaxResidence?: boolean,
}) => {
  const { permissions } = usePermissions();
  const [userData, setUserDataState] = useState<Partial<User>>({
    citizenships: [],
    sin: '',
    yearEnd: '',
    taxNumber: '',
    countryOfTaxResidence: undefined,
    foreignTaxInformation: [],
    customFields: [],
  });
  const setUserData: typeof setUserDataState = (payload) => setUserDataState((previousData) => {
    const newData = typeof payload === 'function' ? payload(previousData) : payload;
    return ({
      ...newData,
      foreignTaxExists: !!newData.foreignTaxInformation?.length,
    });
  });
  const [applicableCountries, setApplicableCountries] = useState<CountryCodes[] | undefined>();

  const [derivedCustomFields, setDerivedCustomFields] = useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] = useState<string[]>();
  const enabledCustomFields = getEnabledCustomFields(options);
  const { activeOrganization } = useContext(UserContext);

  const processUserResponse = (user: Partial<User>) => {
    setUserData({
      sin: undefined,
      type: user.type,
      taxId: user.taxId || undefined,
      taxIdDisclosed: user.taxIdDisclosed || undefined,
      taxIdType: user.taxIdType || undefined,
      yearEnd: user.yearEnd || undefined,
      taxNumber: user.taxNumber || undefined,
      countryOfTaxResidence: user.countryOfTaxResidence || undefined,
      citizenships: user.citizenships || [],
      foreignTaxInformation: omitDeep(user.foreignTaxInformation ?? [], '__typename') as ForeignTaxInformation[],
      foreignTaxExists: user.foreignTaxExists || undefined,
      fatcaStatus: user.fatcaStatus || undefined,
      fatcaStatusOtherDescription: user.fatcaStatusOtherDescription || undefined,
      customFields: user.customFields || [],
    });
    setApplicableCountries(user?.organization?.applicableLocalization?.countries ?? [CountryCodes.CA]);
  };

  const customFieldsPermitted = permissions.includes('read:custom_fields') && enabledCustomFields.length !== 0;

  // loading 1 - custom fields
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.RESIDENCY_INFORMATION,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map((a: any) => (a.key));
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - user's data
  const { loading } = useQuery(FETCH_USER(permissions, derivedCustomFieldsKeys), {
    variables: { userId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: customFieldsLoading,
    onCompleted: (data) => {
      processUserResponse(data.fetchUser.user);
    },
  });

  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER(permissions, derivedCustomFieldsKeys), {
    variables: {
      input: {
        ...omitDeep(userData, ['taxIdDisclosed', 'foreignTaxNumberDisclosed', 'foreignTaxExists']),
        ...(enabledCustomFields && derivedCustomFields && userData?.customFields ? {
          customFields: modifyUpdatingCustomFields(userData.customFields, derivedCustomFields, derivedCustomFieldsKeys),
        } : {}),
        userId,
      },
    },
    onCompleted: (data) => {
      processUserResponse(data.updateUser.user);
      onNext();
    },
  });

  return (
    <ResidencyInformationVisual
      options={options}
      userData={userData}
      updateUser={setUserData}
      continueFunc={updateUser}
      loading={loading || stepLoading || customFieldsLoading}
      updating={updating}
      applicableCountries={applicableCountries}
      grid={grid}
      updateMode={updateMode}
      workflowCompletion={workflowCompletion}
      activeCustomFields={derivedCustomFieldsKeys}
      autoSelectCountryOfTaxResidence={autoSelectCountryOfTaxResidence}
    />
  );
};

export default ResidencyInformation;
